import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { inputCls, KIND_LABEL } from "./_shared";

export default function ContactsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/contacts", { params: debounced ? { q: debounced } : {} })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [debounced]);

  const totalPipeline = items.reduce((s, c) => s + Number(c.total_amount_eur || 0), 0);

  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email);
    toast.success("Email copied");
  };

  return (
    <div data-testid="contacts-admin" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-2">Investor contacts</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter">
            {items.length} contact{items.length === 1 ? "" : "s"} · €{totalPipeline.toLocaleString()} declared
          </h1>
          <p className="text-white/50 font-mono text-[10px] uppercase tracking-widest mt-2">
            Aggregated from every tag-along and invest-anyway submission. De-duped by email.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            data-testid="contacts-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, company, LinkedIn, phone…"
            className={`h-10 w-80 ${inputCls}`}
          />
          <button
            data-testid="contacts-clear"
            onClick={() => setQ("")}
            className="px-3 py-2 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-lime hover:text-lime disabled:opacity-40"
            disabled={!q}
          >
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 font-mono text-sm uppercase tracking-widest py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-white/50" data-testid="contacts-empty">
          {debounced ? `No contact matches "${debounced}".` : "No investor contacts yet."}
        </div>
      ) : (
        <div className="border border-white/10 bg-surface overflow-x-auto" data-testid="contacts-table">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-white/50 text-[10px] font-mono uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-normal">Investor</th>
                <th className="text-left px-4 py-3 font-normal hidden md:table-cell">Company</th>
                <th className="text-left px-4 py-3 font-normal hidden lg:table-cell">Phone</th>
                <th className="text-left px-4 py-3 font-normal">Kinds</th>
                <th className="text-right px-4 py-3 font-normal"># Deals</th>
                <th className="text-right px-4 py-3 font-normal">Total €</th>
                <th className="text-left px-4 py-3 font-normal hidden md:table-cell">Last seen</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const isOpen = expanded === c.email;
                return (
                  <React.Fragment key={c.email}>
                    <tr data-testid={`contact-row-${c.email}`} className="border-t border-white/10 hover:bg-black/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-heading text-sm font-bold uppercase tracking-tight">{c.name || "—"}</div>
                        <button onClick={() => copyEmail(c.email)} className="text-xs text-lime hover:underline block truncate max-w-[220px]" title="Click to copy">
                          {c.email}
                        </button>
                        {c.linkedin && (
                          <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-white/40 hover:text-lime truncate block max-w-[220px]">
                            {c.linkedin}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/70 hidden md:table-cell">{c.company || "—"}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {c.phone ? <a href={`tel:${c.phone}`} className="text-white/70 hover:text-lime">{c.phone}</a> : <span className="text-white/30">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.kinds.map((k) => (
                            <span
                              key={k}
                              className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                                k === "solo_invest" ? "border-lime text-lime bg-lime/10" : "border-white/30 text-white/70"
                              }`}
                            >
                              {KIND_LABEL[k] || k}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-white/80">{c.submissions}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums text-lime font-bold">€{Number(c.total_amount_eur).toLocaleString()}</td>
                      <td className="px-4 py-3 text-white/50 hidden md:table-cell text-[11px] font-mono">
                        {c.last_seen ? new Date(c.last_seen).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`contact-expand-${c.email}`}
                          onClick={() => setExpanded(isOpen ? null : c.email)}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-white/10 hover:border-lime hover:text-lime"
                        >
                          {isOpen ? "Hide" : "History"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`contact-history-${c.email}`} className="border-t border-white/10 bg-black/40">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="space-y-2">
                            {c.interactions.slice().reverse().map((i) => (
                              <div key={i.id} className="flex items-center gap-3 text-xs flex-wrap">
                                <span className="text-white/40 font-mono w-24 shrink-0">{i.created_at ? new Date(i.created_at).toLocaleDateString() : "—"}</span>
                                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                                  i.kind === "solo_invest" ? "border-lime text-lime bg-lime/10" : "border-white/30 text-white/70"
                                }`}>{KIND_LABEL[i.kind] || i.kind}</span>
                                <span className="text-white font-semibold truncate">{i.startup_name}</span>
                                <span className="text-lime font-mono tabular-nums">€{Number(i.amount_eur).toLocaleString()}</span>
                                <span className="text-white/40 font-mono uppercase text-[10px]">{i.status}</span>
                                {i.message && <span className="text-white/50 italic truncate max-w-md">"{i.message}"</span>}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
